import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import {
  App,
  Button,
  Image,
  Modal,
  Space,
  Spin,
  Typography,
  Upload,
} from 'antd';
import { useMemo, useState } from 'react';
import { uploadFileToOss } from '@/utils/oss';

type UploadFileHandler = (file: File) => Promise<string>;

interface BaseImageUploadFieldProps {
  value?: string[];
  onChange?: (value: string[]) => void;
  maxCount?: number;
  maxSizeMB?: number;
  hint?: string;
  showSortTools?: boolean;
  compact?: boolean;
  uploadFile?: UploadFileHandler;
  uploadDir?: string;
}

interface SingleImageUploadFieldProps
  extends Omit<BaseImageUploadFieldProps, 'value' | 'onChange' | 'maxCount'> {
  value?: string;
  onChange?: (value: string) => void;
}

const toUploadFileList = (urls: string[]): UploadFile[] =>
  urls.map((url, index) => ({
    uid: `image-${index}`,
    name: `image-${index + 1}`,
    status: 'done',
    url,
  }));

const BaseImageUploadField = ({
  value,
  onChange,
  maxCount = Number.MAX_SAFE_INTEGER,
  maxSizeMB = 2,
  hint = '建议图片大小不超过 2MB',
  showSortTools = true,
  compact = false,
  uploadFile,
  uploadDir = 'shop/',
}: BaseImageUploadFieldProps) => {
  const { message } = App.useApp();
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const uploadHandler =
    uploadFile ||
    (async (file: File) => {
      const result = await uploadFileToOss(file, {
        dir: uploadDir,
      });
      return result.url;
    });

  const urls = useMemo(() => (value || []).filter(Boolean), [value]);
  const fileList = useMemo(() => toUploadFileList(urls), [urls]);
  const reachedMaxCount = urls.length >= maxCount;

  const emitChange = (nextValue: string[]) => {
    onChange?.(nextValue.slice(0, maxCount));
  };

  const moveItem = (currentIndex: number, nextIndex: number) => {
    if (nextIndex < 0 || nextIndex >= urls.length) {
      return;
    }

    const nextList = urls.slice();
    [nextList[currentIndex], nextList[nextIndex]] = [
      nextList[nextIndex],
      nextList[currentIndex],
    ];
    emitChange(nextList);
  };

  const beforeUpload: UploadProps['beforeUpload'] = (file) => {
    if (!file.type.startsWith('image/')) {
      message.error('只能上传图片文件');
      return Upload.LIST_IGNORE;
    }

    if (file.size / 1024 / 1024 > maxSizeMB) {
      message.error(`图片大小不能超过 ${maxSizeMB}MB`);
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const customRequest: UploadProps['customRequest'] = async (options) => {
    try {
      setUploading(true);
      const url = await uploadHandler(options.file as File);
      const nextList =
        maxCount === 1 ? [url] : [...urls, url].slice(0, maxCount);
      emitChange(nextList);
      options.onSuccess?.({ url });
    } catch (error) {
      options.onError?.(error as Error);
      message.error((error as Error).message || '图片上传失败');
    } finally {
      setUploading(false);
    }
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>{compact ? '上传' : '上传图片'}</div>
    </div>
  );

  return (
    <Space
      direction="vertical"
      size={compact ? 8 : 12}
      style={{ width: '100%' }}
    >
      <Space align="start" wrap>
        <Spin spinning={uploading}>
          <Upload
            accept="image/*"
            beforeUpload={beforeUpload}
            customRequest={customRequest}
            fileList={fileList}
            listType="picture-card"
            maxCount={maxCount}
            showUploadList={{
              showPreviewIcon: true,
              showRemoveIcon: true,
            }}
            onPreview={(file) => setPreviewUrl(file.url || '')}
            onRemove={(file) => {
              const nextList = urls.filter((item) => item !== file.url);
              emitChange(nextList);
              return true;
            }}
          >
            {reachedMaxCount ? null : uploadButton}
          </Upload>
        </Spin>
      </Space>

      {!compact && showSortTools && urls.length > 0 ? (
        <div className="zhiht-upload-sort-list">
          {urls.map((url, index) => (
            <div className="zhiht-upload-sort-item" key={url}>
              <Image
                width={48}
                height={48}
                src={url}
                style={{ borderRadius: 8, objectFit: 'cover' }}
              />
              <Typography.Text ellipsis style={{ flex: 1, minWidth: 0 }}>
                第 {index + 1} 张
              </Typography.Text>
              <Space>
                <Button
                  size="small"
                  icon={<ArrowUpOutlined />}
                  disabled={index === 0}
                  onClick={() => moveItem(index, index - 1)}
                >
                  上移
                </Button>
                <Button
                  size="small"
                  icon={<ArrowDownOutlined />}
                  disabled={index === urls.length - 1}
                  onClick={() => moveItem(index, index + 1)}
                >
                  下移
                </Button>
              </Space>
            </div>
          ))}
        </div>
      ) : null}

      {!compact && hint ? (
        <Typography.Text type="secondary">{hint}</Typography.Text>
      ) : null}

      <Modal
        footer={null}
        open={Boolean(previewUrl)}
        title="图片预览"
        width={720}
        onCancel={() => setPreviewUrl('')}
      >
        <Image src={previewUrl} style={{ width: '100%' }} />
      </Modal>
    </Space>
  );
};

export const MultiImageUploadField = (props: BaseImageUploadFieldProps) => (
  <BaseImageUploadField {...props} />
);

export const SingleImageUploadField = ({
  value,
  onChange,
  ...restProps
}: SingleImageUploadFieldProps) => (
  <BaseImageUploadField
    {...restProps}
    maxCount={1}
    showSortTools={false}
    value={value ? [value] : []}
    onChange={(nextValue) => {
      onChange?.(nextValue[0] || '');
    }}
  />
);

export default BaseImageUploadField;
